/**
 * shared/brand.ts
 *
 * SINGLE SOURCE OF TRUTH for the product name and its derived strings.
 *
 * The platform was renamed Strem → Strenn on 2026-09-22. Before that rename the
 * name existed only as free text: 4,838 literal occurrences across 1,635 files,
 * with no way to change it except a full-repo sweep. This file exists so the
 * next rename is a one-line edit.
 *
 * ## The rule
 *
 * Runtime code (`client/`, `server/`, `shared/`, `tests/`) MUST import from
 * here rather than writing the name as a literal. `scripts/check-brand.mjs`
 * enforces this on push and in CI: a bare `Strenn` in a `.ts`/`.tsx` file under
 * those roots is a gate failure.
 *
 * ## What is deliberately NOT derived from here
 *
 * Surfaces that cannot import TypeScript — `client/index.html`,
 * `client/public/manifest.json`, `package.json`, `.env*`, markdown docs, and
 * the static `__previews__` design fixtures — carry the literal. They are
 * listed in the checker's `LITERAL_ALLOWED` set and re-checked against the
 * values below, so they can drift out of date loudly rather than silently.
 *
 * Also NOT renamed (deliberate, owner directive 2026-09-22):
 *   - `STREM_*` environment variable names. Renaming them requires the hosted
 *     Render env and every local `.env` to change in lockstep; a miss is a
 *     fail-closed boot (`STREM_IDENTIFIER_PEPPER` is load-bearing for Rule 17
 *     identifier counting).
 *   - The `strem_admin` database role and the `strem_auth_test` / `strem_dev` /
 *     `strem_analytics` database names, which need a real migration.
 *
 * (NIST SP 800-63r4 / ISO 27001:2022 Annex A 8.26 — no security invariant
 * depends on this file; it is presentation only.)
 */

/** The bare product name, as shown to users. */
export const PRODUCT_NAME = "Strenn";

/** The full product name, for titles, manifests and legal-ish surfaces. */
export const PRODUCT_FULL_NAME = `${PRODUCT_NAME} Authentication`;

/**
 * Lowercase, hyphen-safe form of {@link PRODUCT_NAME}. Used for CSS/design
 * tokens (`strenn-emerald`), `localStorage` key prefixes (`strenn-activity-*`)
 * and package-style identifiers.
 */
export const PRODUCT_SLUG = "strenn";

/** Primary public domain. */
export const PRODUCT_DOMAIN = "strenn.com.au";

/** Canonical public origin. */
export const PRODUCT_URL = `https://${PRODUCT_DOMAIN}`;

/**
 * The possessive form, pre-built because English makes it a suffix decision
 * rather than a concatenation ("Strenn's", but "Strenns'" for a name ending
 * in s). Keeps copy from hard-coding the apostrophe placement.
 */
export const PRODUCT_NAME_POSSESSIVE = PRODUCT_NAME.endsWith("s")
  ? `${PRODUCT_NAME}'`
  : `${PRODUCT_NAME}'s`;
